/**
 * [Insider+] Security configuration for the Insider+ iframe integration.
 *
 * Iframe origin allow-list, postMessage target derivation, and safe-URL
 * validation for the embedded Insider+ portal.
 */

export const INSIDER_PLUS_BASE_URL = process.env.REACT_APP_INSIDER_PLUS_URL;

/**
 * Get the target origin for postMessage
 * Derived from the Insider Plus base URL so it always matches the iframe
 */
export const getTargetOrigin = () => {
  try {
    return new URL(INSIDER_PLUS_BASE_URL).origin;
  } catch {
    return INSIDER_PLUS_BASE_URL;
  }
};

// Allowed iframe origins for postMessage. Filter falsy values so a missing
// REACT_APP_INSIDER_PLUS_URL doesn't put `undefined` in the allowlist.
export const ALLOWED_IFRAME_ORIGINS = [
  // The configured insider plus origin
  getTargetOrigin(),

  // Development - Only in development mode
  ...(process.env.NODE_ENV === 'development'
    ? [
        'http://localhost:5173',
        'http://localhost:5174',
        'http://localhost:8080',
        'http://localhost:3003',
      ]
    : []),
].filter(Boolean);

/**
 * Validate if origin is allowed to receive messages.
 * Falsy origins (e.g. event.origin === '' from sandboxed/null-origin frames)
 * never match because we already filtered them out of the allowlist, but be
 * explicit to make intent obvious.
 */
export const isIframeOriginAllowed = (origin) =>
  Boolean(origin) && ALLOWED_IFRAME_ORIGINS.includes(origin);

/**
 * Validate that a URL the iframe asked us to navigate to uses http/https.
 * Defense in depth: even though the message comes from an allow-listed origin,
 * never hand a `javascript:`/`data:`/other-scheme URL to window.open or
 * window.location.href.
 */
export const isSafeNavigationUrl = (url) => {
  if (typeof url !== 'string' || url.length === 0) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};
